/**
 * Controller for the "Create workspace" wizard: resolves the current step
 * from the URL (/get-started/setup/:stepId), renders the layout + step
 * header, and wires Continue/Back navigation between steps.
 */
import { useCallback, useEffect, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { SelfServiceLayout } from '@/lib/self-service/components/SelfServiceLayout';
import { CrewStep } from '@/lib/self-service/components/steps/CrewStep';
import { DataStep } from '@/lib/self-service/components/steps/DataStep';
import { InviteStep } from '@/lib/self-service/components/steps/InviteStep';
import { StackStep } from '@/lib/self-service/components/steps/StackStep';
import { TrialStep } from '@/lib/self-service/components/steps/TrialStep';
import {
  findStepIndex,
  FIRST_STEP_PATH,
  GET_STARTED_PATH,
  SELF_SERVICE_STEPS,
  selfServiceStepPath,
} from '@/lib/self-service/constants';
import { SelfServiceFlowProvider } from '@/lib/self-service/flowState';

export function SelfServiceSetupFlow() {
  const { stepId } = useParams<{ stepId: string }>();
  const navigate = useNavigate();

  const stepIndex = findStepIndex(stepId);
  const isLast = stepIndex === SELF_SERVICE_STEPS.length - 1;
  const headingRef = useRef<HTMLHeadingElement>(null);

  const handleContinue = useCallback(() => {
    if (stepIndex >= 0 && !isLast) {
      navigate(selfServiceStepPath(SELF_SERVICE_STEPS[stepIndex + 1].id));
    }
  }, [navigate, stepIndex, isLast]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [stepId]);

  // Unknown step slug → restart at the first step.
  if (stepIndex === -1) {
    return <Navigate to={FIRST_STEP_PATH} replace />;
  }

  const step = SELF_SERVICE_STEPS[stepIndex];
  const backTo =
    stepIndex === 0
      ? GET_STARTED_PATH
      : selfServiceStepPath(SELF_SERVICE_STEPS[stepIndex - 1].id);

  return (
    <SelfServiceFlowProvider>
      <SelfServiceLayout currentStepIndex={stepIndex} backTo={backTo}>
        {/* Step header */}
        <div className='mb-8 flex flex-col'>
          <p
            role='status'
            aria-live='polite'
            className='text-sm font-medium text-[var(--color-text-primary)]'
          >
            Step {stepIndex + 1} of {SELF_SERVICE_STEPS.length}
          </p>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className='mt-2 font-display text-4xl font-medium text-brand-600 dark:text-brand-400'
          >
            {step.title}
          </h1>
          {step.subtitle && (
            <p className='mt-3 text-base text-[var(--color-text-tertiary)]'>
              {step.subtitle}
            </p>
          )}
        </div>

        {/* Step body. The trial step is last and owns the confirm action, so
            it takes no onContinue — it leaves the SPA for Stripe Checkout. */}
        {step.id === 'crew' && <CrewStep onContinue={handleContinue} />}
        {step.id === 'data' && <DataStep onContinue={handleContinue} />}
        {step.id === 'stack' && <StackStep onContinue={handleContinue} />}
        {step.id === 'team' && <InviteStep onContinue={handleContinue} />}
        {step.id === 'trial' && <TrialStep />}
      </SelfServiceLayout>
    </SelfServiceFlowProvider>
  );
}
